/**
 * - `unavailable`: the source isn't there — most often a video still encoding on Bunny
 *   (its HLS manifest 4xxs), reached right after posting or via a shared link. Retrying
 *   won't help yet.
 * - `network`: the connection failed, timed out, or the server did. Nothing is wrong with
 *   the video, so this is a "try again", same as a stall.
 * - `other`: decode/playback failures — would fail identically on retry.
 *
 * Deliberately narrow: `unavailable` shows "this video isn't ready yet", a claim about the
 * backend. Matching on hls.js's detail names alone made a timeout (`manifestLoadTimeOut`) or a
 * dropped connection (`manifestLoadError` with status 0) claim the video was still encoding.
 */
export type VideoErrorKind = 'unavailable' | 'network' | 'other'

type HlsErrorData = {
  type?: string
  details?: string
  response?: { code?: number }
}

type NativeErrorEvent = {
  target?: { error?: { code?: number } }
}

// `MediaError.MEDIA_ERR_NETWORK`
const MEDIA_ERR_NETWORK = 2

export function classifyVideoError(error: unknown): VideoErrorKind | null {
  if (error === null || error === undefined) return null
  if (typeof error !== 'object') return 'other'

  // hls.js (Chrome, Firefox): the `ERROR` event data.
  const hls = error as HlsErrorData
  const status = hls.response?.code

  if (typeof status === 'number' && status >= 400 && status < 500) return 'unavailable'
  if (typeof status === 'number' && status >= 500) return 'network'
  if (hls.type === 'networkError') return 'network'

  // Native playback (Safari's own HLS): the DOM `error` event.
  const native = (error as NativeErrorEvent).target?.error
  if (native?.code === MEDIA_ERR_NETWORK) return 'network'

  return 'other'
}
